import { Component, OnInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { PlaceService } from '@app/modules/model/svcplace';

import { AppConstants } from '@app/app.constants';
import { languageService, languageSupport } from '@app/modules/common/language';
import { platformService } from '@app/modules/common/platform';
import { deviceService } from '@app/modules/sockets';
import { syncService } from '@app/modules/sync';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { UiRaspPIDevices } from '../devices/ui-devices';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-rasppi-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiRaspPIEdit extends languageSupport  implements OnInit, OnDestroy { 
    @Input('place') _place: Place = null;
    @Input('active') _active = false;

    private _service_subscription = null;
    private _wsserver_subscription = null;
    private _wsserver_connection = null;
    private _wsserver_check_interval = null;
    private _place_subscription = null;

    /*****************************/
    /* COMPONENT DATA SHARING    */
    /*****************************/

    DiscardChanges(){
        this._svccache.clear();
        this._selected = null;

        this.LoadServiceForm();
        if (this._devicesctrl){
            this._devicesctrl.DiscardChanges();
        }
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.markForCheck();
        this._onchange.emit();
    }

    get IsValid(){
        if (!this.ServiceForm.valid){
            return false;       // invalid form data
        } 

        if (this.ServiceForm.get('connected').value && !this.Selected){
            return false;       // service not available
        }

        if (this._devicesctrl && !this._devicesctrl.IsValid){
            return false;       // invalid devices
        }

        return true;
    }

    private _isdirty = false;
    get IsDirty(){
        let _isdirty = (this._isdirty || (this.ServiceForm.dirty));
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }

        _isdirty = _isdirty || (this._devicesctrl && this._devicesctrl.IsDirty)

        return _isdirty;
    }

    set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.ServiceForm.markAsPristine();
        }

        if (this._devicesctrl){
            this._devicesctrl.IsDirty = false;
        }
    }

    get EditDevice(){
        return this._devicesctrl?.EditDevice;
    }

    set EditDevice(value){
        if (this._devicesctrl){
            this._devicesctrl.EditDevice = value;
        }
    }

    /*****************************/
    /* COMPONENT DATA ACCESS     */
    /*****************************/

    private _selservice = null;     // currently selected service
    private _svccache = new Map <string, PlaceService> ();

    private get _selected() : PlaceService {
        return this._selservice;
    }

    private set _selected(value: PlaceService){
        if (this._selservice != value) {
            // apply current changes and store it on the cache
            if (this._selected && value && (value.name != null)){
                if (this._devicesctrl){
                    this._devicesctrl.ApplyChanges();
                }

                this._svccache.set(this._selected.name, this._selected);
            }

            // update the selected service entry
            this._selservice = value;
            this.change.markForCheck();
        }
    }

    get Selected(){
        if (this._selected == null){
            let _selectfnc = () => {
                let _servicename = this.ServiceForm.get("serviceid").value;
                if (_servicename){

                    // first, look up in the place services
                    for(let _service of this._place.services){
                        if (_service.IsValid && (_service.name == _servicename)){
                            return _service;
                        }
                    }

                    // next: if connected to service, create the entry
                    if (this.ServiceForm.valid && this.ServiceForm.get('connectws').value && this.ServiceForm.get('servicews').value){
                        let _localsvc = new PlaceService(null, this.data);
                        if (_localsvc ){
                            _localsvc .status = 'AC';
                        }
    
                        return _localsvc;
                    }

                    // not found: new or non existant service        
                    return null;    
                }

                // if no service name, then create a new one by default
                else {
                    let _localsvc = new PlaceService(null, this.data);
                    if (_localsvc){
                        _localsvc.status = 'AC';
                    }

                    return _localsvc;
                }
            }

            let _targetsvc = _selectfnc();
            if (_targetsvc != this._selservice){
                // subscribe to service updates
                if (this._service_subscription){
                    this._service_subscription.unsubscribe();
                    this._service_subscription = null;
                }
    
                if (_targetsvc){
                    this._service_subscription = _targetsvc.OnRefresh.subscribe(
                    data => {
                        this.change.markForCheck();
                    });
                }
            }


            this._selected = _targetsvc;
        }

        return this._selected;
    }

    private get WSOnline(){
        return this._selected?.IsOnline;
    }

    private set WSOnline(value){
        if (!value){
            this._connection = null;
        }

        if (this._selected){
            let _online = this._selected.IsOnline;
            if (_online != value){
                this._selected.IsOnline = value;
            }
        }

        this.change.detectChanges();    
    }

    private _wsversion = null;
    get WSVersion(){
        return this._wsversion;
    }

    private _connecting = null;    // avoid testing multiple times
    private _connection = null;    // the direct connection instance
    private _lservicews = null;    // last (checked) connection string

    async RunCheck(){  
        let name = this.ServiceForm.get('serviceid').value;
        
        let _promise = null;
        if (this.ServiceForm.get('connectws').value) {
            let _servicews = this.ServiceForm.get('servicews').valid ? this.ServiceForm.get('servicews').value : null;
            if (!_servicews){
                this.WSOnline = false;
            }
            else {
                if (this.WSOnline && (this._lservicews == _servicews)){
                    return;     // direct connection is alive (no need to check again)
                }

                if (this._connecting){
                    clearTimeout(this._connecting);
        
                    this._connecting = setTimeout(() => {
                        return this.RunCheck();
                    }, 500);    // retry after 500ms
        
                    return;
                }
    
                let _parts = _servicews.split(':');
                let host = _parts[0];
                let port = _parts[1];

                _promise = new Promise <boolean> ((resolve) => {
                    this._connecting = setTimeout(() => { /* empty */ }, 0);
    
                    if (this._wsserver_connection){
                        this._wsserver_connection.Disconnect();
                        this._wsserver_connection = null;
                    }
    
                    this._lservicews = _servicews;
                    
                    this.devsvc.Connect(host, port, name, true).then(
                    connection => {
                        this._connecting = null;

                        if (connection){
                            this._connection = connection;

                            if (connection.svcname != name){
                                this._ServiceNameChanged(connection.svcname);
                            }
    
                            this._wsversion = connection.version;
                            if (this._wsserver_subscription){
                                this._wsserver_subscription.unsubscribe;
                                this._wsserver_subscription = null;
                            }
        
                            this._wsserver_subscription = connection.OnDisconnected.subscribe(
                            () => {
                                this.WSOnline = false;
                            });
    
                            this._wsserver_connection = connection;
                        }
    
                        resolve(connection != null);
                    });
                })        
            }
        }
        else {
            _promise = new Promise <boolean> ((resolve) => {
                this.devsvc.Connect(null, null, name, false).then(
                connection => {
                    if (connection){
                        this._wsversion = connection.version;
                    }
                    resolve(connection != null);
                })
            });
        }
        
        this.WSOnline = await _promise;   // true if connected     
    }      

    get ServiceStatus() {
        if (!this._serviceForm || this._connecting){
            return 'CHECK';
        }

        if (!this.ServiceForm.valid){
            let _serviceid = this.ServiceForm.get('serviceid');
            if (!_serviceid.valid){
                return 'CHECK';
            }

            let _servicews = this.ServiceForm.get('servicews');
            if (!_servicews.valid){
                return 'OFFLINE';
            }
        }

        return (this.WSOnline) ? 'ONLINE' : 'OFFLINE';
    }
            
    private _serviceForm = null;   
    get ServiceForm(){
        if (this._serviceForm == null){
            this.LoadServiceForm();
        }
        return this._serviceForm;
    }

    private _checking = null;       // avoid testing multiple times
    private checkAvailableService(control: FormControl): any {
        if (this._checking){
            clearTimeout(this._checking);

            this._checking = setTimeout(() => {
                this.checkAvailableService(control);
            }, 500);

            return;
        }
        else {
            let _service = control.value;
            if (_service){
                this._checking = setTimeout(() => { /* empty */ }, 0);

                this.devsvc.Available(this.sync, this._place.objref, _service).then(
                data => {
                    this._checking = null;

                    control.setErrors(null);
                    if (!data && control.valid){
                        control.setErrors({ available: true });  
                    }

                    this.change.markForCheck();
                });        
            }
        }
    }

    private checkServiceConnection(control: FormControl): any {
        let _wsurl = control.value;
        if (_wsurl){
            let _parts = _wsurl.split(':');
            if (_parts.length !== 2) {
                control.setErrors({ format: true }); 
            }
            else {
                let _addr = _parts[0].trim()
                let _port = _parts[1].trim();
                
                // test that the port value is valid
                if ((Number(_port) < 1) || (Number(_port) > 65535)){
                    control.setErrors({ format: true }); 
                }
                else {
                    let _regex_lc = /^localhost$/
                    let _regex_ip = /^((25[0-5]|(2[0-4]|1[0-9]|[1-9]|)[0-9])(\.(?!$)|$)){4}$/;
                    let _regex_ad = /^(https?:\/\/)?([a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,})$/;    

                    if (!(_regex_lc.test(_addr) || _regex_ip.test(_addr) || _regex_ad.test(_addr))){
                        control.setErrors({ format: true }); 
                    }
                    else {
                        if (this._serviceForm){     // must be loaded
                            this.RunCheck();
                        }
                    }
                }
            }
        }

        return null;
    }    

    private get _IsConnected(){
        // this connection has a service (is connected)
        if (this._place.MultiplePOS){
            return !!(this._place.till?.connect.hassvc);
        }

        // al least one connection has a service (is connected)
        else {  
            return this._place.till?.connected.some(
            _connect => {
                return _connect.hassvc;
            });
        }
    }

    private LoadServiceForm(){
        let _serviceid = null;
        let _connected = false;
        let _connectws = false; 
        let _servicews = null;
        
        let _localsvc = this._place.till;
        if (_localsvc){
            _serviceid = _localsvc.name;

            _connected = this._IsConnected;
            if (_connected){
                _connectws = _localsvc.ws != null;      // true if this device is connected 
                _servicews = _localsvc.ws || null;      // this device connection string  
            }
        }

        if (this._serviceForm == null){
            this._serviceForm = new FormGroup({
                serviceid: new FormControl(_serviceid, [
                    Validators.required
                ]),
                connected: new FormControl(_connected, [
                    /* no validators */
                ]),
                connectws: new FormControl(_connectws, [
                    /* no validators */
                ]),
                servicews: new FormControl(_servicews, [
                    Validators.required,
                    this.checkServiceConnection.bind(this)                
                ]),
            });
        }
        else {
            this._serviceForm.patchValue({
                serviceid: _serviceid,
                connected: _connected,
                connectws: _connectws,
                servicews: _servicews
            });
        }

        this.OnServiceConnectChange();
        this.OnServiceDirectChange();

        this.IsDirty = false;
        setTimeout(() => {
            this.IsDirty = false;
        }, 0);

        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private platform: platformService, private devsvc: deviceService, private sync: syncService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _ReloadPlace(){
        this._selected = null;
        this.LoadServiceForm();
    }

    ngOnInit(){
        if (this._wsserver_check_interval == null){
            this._wsserver_check_interval = setInterval(() => {
                if (this._active){  // do not check if not active
                    this.RunCheck();
                }
            }, 2500);
        }

        this._place_subscription = this._place.OnRefresh.subscribe(
        data => {
            if (!this.IsDirty && this.IsValid){
                this._ReloadPlace();
            }
        });
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._wsserver_subscription){
            this._wsserver_subscription.unsubscribe();
            this._wsserver_subscription = null;
        }

        if (this._wsserver_connection){
            this._wsserver_connection.Disconnect();
            this._wsserver_connection = null;
        }        

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }

        if (this._wsserver_check_interval){
            clearInterval(this._wsserver_check_interval);
            this._wsserver_check_interval = null;
        }
    }

    ApplyChanges(){
        this._svccache.clear();

        let _serviceid = this.ServiceForm.get('serviceid').value;
        let _servicews = this.ServiceForm.get('connectws').value ? this.ServiceForm.get('servicews').value : null;
        let _connected = this.ServiceForm.get('connected').value
        
        let _selectsvc = this.Selected;

        let _svcchange = !_selectsvc.connect || (_serviceid != _selectsvc.name) || (_servicews != _selectsvc.ws) || (this._place.till != _selectsvc);
        if (_svcchange){    // service must be updated
            this._ChangeService(_serviceid, _servicews, _connected);
        }

        if (_selectsvc.connect){
            _selectsvc.connect.hassvc = _connected;
        }

        // add the changed service to the place
        let _exists = this._place.services.includes(_selectsvc);
        if (!_exists){
            this._place.AddService(_selectsvc);
        }

        if (this._devicesctrl){
            this._devicesctrl.ApplyChanges();
        }

        this.IsDirty = false;
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    private _ServiceNameChanged(value){
        let _serviceid = this.ServiceForm.get('serviceid');
        if (_serviceid.value != value){
            _serviceid.setValue(value);
            
            _serviceid.setErrors(null);
            if (value == null){
                _serviceid.setErrors({required: true });
            }
            else {
                this.checkAvailableService(_serviceid);
            }
        }

        this.change.markForCheck();
    }

    OnServiceNameChanged(){
        let _servicename = this.ServiceForm.get("serviceid").value;
        if (this._selected && (this._selected.name == _servicename)){
            return;     // no name change
        }

        // try to find the service in the cache
        if (_servicename && this._svccache.has(_servicename)){
            this._selected = this._svccache.get(_servicename)
        }
        else {
            this._selected = null;  // service name is not found
        }

        this.change.markForCheck();
    }   

    OnNewService(){
        let _localsvc = null;

        let _serviceid = this.ServiceForm.get('serviceid').value;
        if (_serviceid){
            _localsvc = new PlaceService(null, this.data);
            if (_localsvc){
                _localsvc.name = _serviceid;
                _localsvc.place = this._place;
                _localsvc.status = 'AC';
            }    
        }

        this._selected = _localsvc;
        this.change.markForCheck();
    }

    private _ChangeService(_serviceid, _servicews = null, _connected = false){
        let _oldservice = this._place.till;
        let _newservice = this._selected;

        if (_newservice){
            _newservice.name = _serviceid;
            _newservice.place = this._place;
            _newservice.status = 'AC';
        }

        // update / create the service connection
        let _connect = _oldservice?.connect || _newservice.DoConnect();

        _connect.hassvc = _connected;
        _connect.connect = _connected ? _servicews : null;
        _connect.rasppi = _newservice;

        // if single POS then delete prevous service
        if (_newservice && (_oldservice != _newservice) && !this._place.MultiplePOS){
            if (_oldservice && (_oldservice != _newservice)){
                for (let _connect of _oldservice.connects){
                    if (_connect.IsValid){
                        _connect.rasppi = _newservice;
                    }
                }
    
                _oldservice.status = 'DE';
            }
        }

        this._place.DoRefresh('RASPPI');
    }
    
    private _downloadcheck = null;
    get CanDownload(){
        if (this._downloadcheck === null){
            this._downloadcheck = false;    // avoid reentrance

            let _runos = this.platform.RunningOS;
            if (_runos){
                let _url = AppConstants.baseURL + 'sockets/install/' + _runos.syst + "/" + _runos.arch + "/" + _runos.proc + "/install" + _runos.fext;
                try {
                    fetch(_url, { method: 'HEAD' }).then(
                        response => {
                            this._downloadcheck = response.ok;
                            if (!this._downloadcheck){
                                console.warn("[RASPPI] installer not found at '" + _url + "'");
                            }
                            
                            this.change.markForCheck();
                        })
                        .catch(error => {
                            console.warn("[RASPPI] installer not found at '" + _url + "'");
                            this._downloadcheck = false;

                            this.change.markForCheck();
                        });                                            
                } 
                catch (error) {
                    console.warn("[RASPPI] installer not found at '" + _url + "'");
                    this._downloadcheck = false;

                    this.change.markForCheck();            
                }
            }
            else {  // cannot determine running OS
                console.warn("[RASPPI] cannot determine running OS, or not valid to install");
            }
        }

        return this._downloadcheck;
    }

    OnDownload(){
        let _path = null;
        let _file = null;

        let _runos = this.platform.RunningOS;
        if (_runos){
            _file = "install" + _runos.fext;
            _path = _runos.syst + "/" + _runos.arch + "/" + _runos.proc + "/" + _file; 
        } 

        if (_path){
            const _link = document.createElement('a');
            document.body.appendChild(_link);
        
            _link.href = AppConstants.baseURL + 'sockets/install/' + _path;
            _link.target = '_self';
            _link.download = _file;
            _link.click(); 
    
            _link.remove();       
        }
    }

    OnServiceConnectChange(){
        let _serviceid = this.ServiceForm.get('serviceid');

        if (this.ServiceForm.get('connected').value == false){
            _serviceid.clearValidators();
            _serviceid.setErrors(null);
            _serviceid.updateValueAndValidity();    
        }
        else {
            _serviceid.setValidators([
                Validators.required,
            ]);

            _serviceid.setErrors(null);
            _serviceid.updateValueAndValidity();    
        }
    }

    OnServiceDirectChange(){
        let _servicews = this.ServiceForm.get('servicews');

        if (this.ServiceForm.get('connectws').value == false){
            if (this._connection){
                this._connection.Disconnect();
                this._connection = null;
            }

            _servicews.clearValidators();
            _servicews.setErrors(null);
            _servicews.updateValueAndValidity();    
        }
        else { 
            _servicews.setValidators([
                Validators.required,
                this.checkServiceConnection.bind(this)    
            ]);

            if (_servicews.value == null){
                _servicews.setValue('localhost:5005');
            }
            
            _servicews.setErrors(null);
            _servicews.updateValueAndValidity();    
        }    
    }

    /****************************/
    /* CONNECTED DEVICES        */
    /****************************/

    @ViewChild('devices', { static: false }) _devicesctrl: UiRaspPIDevices;
    get DeviceProducts(){
        if (this._devicesctrl) {
            return this._devicesctrl.DeviceProducts;
        }

        return null;
    }

    OnAddProducts(products){
        if (this._devicesctrl){
            this._devicesctrl.OnAddProducts(products);
        }

        this.change.markForCheck();
    }

    OnHideProducts(){
        if (this._devicesctrl){
            this._devicesctrl.OnHideProducts();
        }

        this.change.markForCheck();
    }
}